import React, { type FC, type JSX } from 'react';
import { formatDateShort, formatTimeTwoDigit } from '@/app/components/search/utils/dates/utilDates';
import getSearchParams from '@/app/components/search/common/getSearchParams';
import { useTranslation } from '@/app/components/translations/Translation';
import { Text } from '@lmn/design-system';
import {
  TransportBaggageContainer,
  FlightContainer,
  TransportInfo,
  TransportInfoAirports,
  TransportInfoTime,
  TransportSlimTitle,
} from './styled';
import { calcDeltaDays, WayStops } from '../Transport/utils';
import { useIsLgOrGreater } from '../../../hooks/useMediaSSR';
import type { TransportProps } from '..';
import Baggage from '../Baggage';

const TransportSlim: FC<TransportProps> = (props): JSX.Element => {
  const isMobile = !useIsLgOrGreater();
  const {
    isOutBound,
    departureDate,
    arrivalDate,
    departureAirportCode,
    departureCity,
    arrivalAirportCode,
    arrivalCity,
    numberOfStops,
    durationInMinutes,
    locale,
    isWaitingState,
    dictionary,
    dealsCount,
    baggage,
    differentDepartureAirportCodes,
    differentArrivalAirportCodes,
    numberOfBaggagePiecesIncluded,
  } = props;
  const { t } = useTranslation();
  const inbound = t('vacation.oss.flights_details.inbound');
  const outbound = t('vacation.oss.flights_details.outbound');
  const showDetails = !isWaitingState && dealsCount > 0;
  const showLoader = !showDetails;
  const searchParams = getSearchParams();
  const { datefrom, dateto } = searchParams;
  const title = `${isOutBound ? outbound : inbound} ${formatDateShort(
    locale,
    isOutBound ? datefrom : dateto
  ).toUpperCase()}`;

  const days = departureDate && arrivalDate ? calcDeltaDays(departureDate, arrivalDate) : null;
  const stops = WayStops({ numberOfStops, dictionary });
  const flightInfoTimeText = `${formatTimeTwoDigit(departureDate, locale)} – ${formatTimeTwoDigit(
    arrivalDate,
    locale
  )}`;

  const TransportInfoAirportsText = (
    <>
      <span
        className={
          (isOutBound && differentDepartureAirportCodes) || (!isOutBound && differentArrivalAirportCodes)
            ? 'warning'
            : ''
        }
      >
        ({departureAirportCode})
      </span>
      {` ${departureCity} - `}
      <span
        className={
          (!isOutBound && differentDepartureAirportCodes) || (isOutBound && differentArrivalAirportCodes)
            ? 'warning'
            : ''
        }
      >
        ({arrivalAirportCode})
      </span>
      {` ${arrivalCity}`}
    </>
  );

  return (
    <FlightContainer>
      {!isMobile && <TransportSlimTitle>{title}</TransportSlimTitle>}
      <TransportInfo>
        <TransportInfoTime isWaiting={showLoader}>
          {showLoader ? <>&nbsp;</> : flightInfoTimeText}
          {days && days > 0 ? <b>+{days}</b> : null}
        </TransportInfoTime>
        <TransportInfoAirports isWaiting={showLoader}>
          {showLoader ? <>&nbsp;</> : TransportInfoAirportsText}
        </TransportInfoAirports>
      </TransportInfo>
      <TransportBaggageContainer isWaiting={showLoader}>
        {showLoader ? (
          <div>&nbsp;</div>
        ) : (
          <Text
            variant="BodySmall"
            color="textSecondary"
          >{`${stops} · ${Math.floor(durationInMinutes / 60)}h ${durationInMinutes % 60}m`}</Text>
        )}
        {showLoader ? (
          <div>&nbsp;</div>
        ) : (
          <Baggage baggage={baggage} numberOfBaggagePiecesIncluded={numberOfBaggagePiecesIncluded} />
        )}
      </TransportBaggageContainer>
    </FlightContainer>
  );
};

export default TransportSlim;
